

import { useEffect, useState } from "react";
import { Receipt } from "lucide-react";
import { Alert, Button, DataTable, Drawer, LoadingState, TableEmpty, type Column } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import type { StoreSubscriptionPaymentView } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useStoreSubscriptionPaymentsQuery } from "@/shared/stores/services/queries/useStoreSubscriptionPaymentsQuery";
import { SubscriptionPaymentProofModal } from "./SubscriptionPaymentProofModal";

type Props = {
  storeId: string;
  locale: string;
  triggerVariant?: "icon" | "menu";
  triggerClassName?: string;
  triggerLabel?: string;
  onOpen?: () => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
};

export function StorePaymentsModal({
  storeId,
  locale,
  triggerVariant = "icon",
  triggerClassName,
  triggerLabel,
  onOpen,
  isOpen: controlledOpen,
  onOpenChange,
  hideTrigger,
}: Props) {
  const t = useTranslations();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const {
    data: paymentsData,
    isLoading,
    error: paymentsError,
    refetch,
  } = useStoreSubscriptionPaymentsQuery({
    storeId: isOpen ? storeId : "",
  });

  const formatDate = (value: string | null) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
  };

  const formatAmount = (amount: number | null | undefined, currency?: string) => {
    if (amount === null || amount === undefined) {
      return "-";
    }
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency || "EGP",
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${amount} ${currency ?? ""}`.trim();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const timer = window.setTimeout(() => {
      void refetch();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isOpen, refetch]);

  const error =
    paymentsError instanceof ApiJsonClientError
      ? paymentsError.message || t(keys.errors.generic)
      : null;
  const payments = (paymentsData ?? []) as StoreSubscriptionPaymentView[];

  const openModal = () => {
    setOpen(true);
    onOpen?.();
  };

  const label = triggerLabel ?? t(keys.admin.storeSubscriptionsPaymentsAction);
  const columns: Array<Column<StoreSubscriptionPaymentView>> = [
    {
      key: "amount",
      label: t(keys.admin.subscriptionPaymentAmountLabel),
      render: (payment) => (
        <span className="rd-num" style={{ fontWeight: 600 }}>
          {formatAmount(payment.amount, payment.currency)}
        </span>
      ),
    },
    { key: "paid", label: t(keys.admin.subscriptionPaymentPaidAt), render: (payment) => formatDate(payment.paidAt) },
    {
      key: "next",
      label: t(keys.admin.subscriptionPaymentNextAt),
      render: (payment) => (
        <span style={{ color: "var(--rd-text-secondary)" }}>{formatDate(payment.scheduleDueDate ?? null)}</span>
      ),
    },
    // Null while a seller's top-up request is still awaiting review — nobody
    // has settled it yet.
    {
      key: "by",
      label: t(keys.admin.subscriptionPaymentSettledBy),
      render: (payment) => payment.settledByAdmin?.name ?? "—",
    },
    {
      key: "proof",
      label: t(keys.admin.subscriptionPaymentProof),
      align: "end",
      width: 1.2,
      render: (payment) =>
        payment.proofObjectKey ? (
          <SubscriptionPaymentProofModal storeId={storeId} paymentId={payment.id} />
        ) : (
          <span style={{ fontSize: 13, color: "var(--rd-text-tertiary)" }}>
            {t(keys.admin.subscriptionPaymentProofMissing)}
          </span>
        ),
    },
  ];

  return (
    <>
      {hideTrigger ? null : triggerVariant === "menu" ? (
        <Button variant="ghost" size="sm" className={triggerClassName} onClick={openModal}>
          {label}
        </Button>
      ) : (
        <Button variant="ghost" iconOnly icon={Receipt} title={label} className={triggerClassName} onClick={openModal} />
      )}

      <Drawer
        open={isOpen}
        onClose={() => setOpen(false)}
        closeLabel={t(keys.common.close)}
        badge="currency"
        title={t(keys.admin.subscriptionPaymentsTitle)}
        sub={t(keys.admin.subscriptionPaymentsHint)}
        width={580}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {error ? (
            <Alert tone="error">
              {error}
              <span style={{ display: "block", marginTop: 2 }}>{t(keys.errors.recovery)}</span>
            </Alert>
          ) : null}
          {isLoading ? (
            <LoadingState title={t(keys.shell.loadingTitle, { page: t("admin.paymentsStatePage") })} sub={t(keys.shell.loadingSub)} rows={0} />
          ) : (
            <DataTable
              label={t(keys.admin.subscriptionPaymentsTitle)}
              columns={columns}
              rows={payments}
              rowKey={(payment) => payment.id}
              minWidth={620}
              empty={<TableEmpty title={t("admin.subscriptionPaymentsEmptyTitle")}>{t("admin.subscriptionPaymentsEmptyText")}</TableEmpty>}
            />
          )}
        </div>
      </Drawer>
    </>
  );
}
